/**
 * Turning on a camera or microphone fails for several distinct reasons, and the
 * remedy differs each time. The raw DOMException name is useless to a user, so
 * it is translated here once and reused by the studio and the guest page.
 */
export function describeDeviceError(cause: unknown, kind: 'camera' | 'microphone'): string {
  const name = cause instanceof DOMException ? cause.name : '';
  switch (name) {
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `No ${kind} was found on this device.`;
    case 'NotAllowedError':
    case 'SecurityError':
      return `The browser blocked access to the ${kind}. Allow it in the address bar and try again.`;
    case 'NotReadableError':
    case 'AbortError':
      return `The ${kind} is already in use by another application or browser tab.`;
    default:
      return `Could not start the ${kind}.`;
  }
}

export interface InputDevices {
  video: MediaDeviceInfo[];
  audio: MediaDeviceInfo[];
}

/**
 * Lists the camera and microphone inputs.
 *
 * `enumerateDevices` returns entries with **empty labels** until the page has
 * been granted media permission at least once, so a probe stream is acquired
 * first and released immediately. Without it the pickers read "Camera",
 * "Camera", "Camera" and the host cannot tell a capture card from a webcam.
 */
export async function listInputDevices(): Promise<InputDevices> {
  let probe: MediaStream | undefined;
  try {
    probe = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  } catch {
    // Permission refused, or no device at all. Enumerate anyway: the labels
    // will be empty, but a device the browser can see is still selectable.
  } finally {
    probe?.getTracks().forEach((track) => track.stop());
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  return {
    video: devices.filter((device) => device.kind === 'videoinput'),
    audio: devices.filter((device) => device.kind === 'audioinput'),
  };
}

/** A device label, or a stable fallback when permission has not been granted. */
export function deviceLabel(device: MediaDeviceInfo, fallback: string, position: number): string {
  return device.label || `${fallback} ${position}`;
}
